import { AdTypeOption, AdTypeOptions } from '@/_shared/types'
import { MasonryComponent, TypedResponsiveMasonry } from '@/components/ResponsiveMasonryWrapper.tsx'
import { Stack, Text } from '@/components/custom-components'
import { FeatureTabs } from '@/components/custom-components/FeatureTabs'
import { Loader } from '@/components/custom-components/Loader'
import { ErrorDisplay } from '@/components/error.tsx'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { useSidebar } from '@/components/shadcn/sidebar'
import { AdCard, DiscoverAdCard } from '@/components/templates/AdCard'
import useCardLayoutWithImpressions from '@/hooks/useCardLayoutWithImpressions.tsx'
import { trpc } from '@/utils/trpc.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer' // <-- import useInView

const LIMIT = 10

type SearchParams = {
  sideBarOpen?: boolean
}

export const Route = createFileRoute('/feeds/saved/ads')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean
    return {
      sideBarOpen,
    }
  },
})

function All() {
  const navigate = useNavigate()
  const { open: sidebarOpen } = useSidebar()

  const {
    data: savedAdsPages,
    isLoading: adsLoading,
    isError: adsError,
    isFetchingNextPage: isFetchingNextAdsPage,
    fetchNextPage: fetchNextAdsPage,
    hasNextPage: hasNextAdsPage,
    isRefetching: adsRefetching,
  } = trpc.getAllSavedAdsPaginated.useInfiniteQuery(
    { limit: LIMIT },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  const { squareRef, desiredCardWidth, columns, gutterWidth } = useCardLayoutWithImpressions()

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
  })

  // Fetch next page when reaching the bottom
  useEffect(() => {
    if (inView && hasNextAdsPage && !isFetchingNextAdsPage) {
      if (hasNextAdsPage && !isFetchingNextAdsPage && (savedAdsPages?.pages?.at(-1)?.results?.length ?? 0) >= LIMIT) {
        fetchNextAdsPage()
      }
    }
  }, [inView, hasNextAdsPage, isFetchingNextAdsPage, fetchNextAdsPage])

  const allSavedAds = savedAdsPages?.pages.flatMap((page) => page.results) || []

  const noAdsAvailable = allSavedAds.filter((data) => !data.adTemplate).length === 0

  // THIS IS THE START OF THE STUFF TEMPLATE RELATED DELETED IF SWITCH BACCK

  const {
    data: savedAdTemplatesPages,
    isLoading: templatesLoading,
    isError: templatesError,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = trpc.getAllSavedAdTemplatesPaginated.useInfiniteQuery(
    { limit: LIMIT },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  // Fetch next page when reaching the bottom
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      if (hasNextPage && !isFetchingNextPage && (savedAdTemplatesPages?.pages?.at(-1)?.results?.length ?? 0) >= LIMIT) {
        fetchNextPage()
      }
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  const savedAdTemplates = savedAdTemplatesPages?.pages.flatMap((page) => page.results) || []

  const noTemplatesAvailable = savedAdTemplates.length === 0

  // THIS IS THE END OF THE STUFF TEMPLATE RELATED DELETED IF SWITCH BACCK

  if (adsError || templatesError) {
    return (
      <div className={'px-0'}>
        <ErrorDisplay />
      </div>
    )
  }

  if (!savedAdsPages || adsLoading || adsRefetching || templatesLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen m-auto">
        <Loader />
      </div>
    )
  }

  return (
    <Stack className="gap-3 lg:gap-8">
      <Stack className="gap-3 lg:gap-8">
        <div className="flex justify-between">
          <div>
            <Text weight="semibold" size="xxl">
              {sidebarOpen ? 'Kept Safe. Ready Anytime.' : 'All Saved: Kept Safe. Ready Anytime.'}
            </Text>
            <p className="text-sm lg:text-base text-thememutedforeground mt-2">
              Store and revisit your favorites whenever inspiration strikes.
            </p>
          </div>

          <div className="flex items-center gap-2 justify-end flex-wrap">
            <FeatureTabs
              tabItems={[
                { name: 'Ads', link: '/feeds/saved/ads' },
                { name: 'Landers', link: '/feeds/saved/ad-landers' },
                { name: 'Emails', link: '/feeds/saved/emails' },
              ]}
            />
            <Select
              value={AdTypeOptions.ALL_SAVED}
              onValueChange={(value: AdTypeOption) => {
                if (value === AdTypeOptions.ONLY_TEMPLATES) {
                  navigate({
                    to: `/feeds/saved/ad-templates`,
                  })
                }
              }}
            >
              <SelectTrigger className="w-33 h-8 max-h-8">
                <SelectValue placeholder="Ad Type" className="rounded-md" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(AdTypeOptions).map((item) => (
                    <SelectItem key={item} value={item} className="py-1.5">
                      {item}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Stack>

      <div className="">
        {noAdsAvailable && noTemplatesAvailable ? (
          <div className="lg:h-96 flex justify-center items-center">
            <p>No ad added to this collection</p>
          </div>
        ) : (
          <div ref={squareRef} className="relative w-full pb-10">
            <TypedResponsiveMasonry columnsCountBreakPoints={columns ? { 0: columns } : {}}>
              <MasonryComponent gutter={gutterWidth / 16 + 'rem'}>
                {/* Render saved Ads */}
                {(allSavedAds ?? []).map((ad) => {
                  if (ad.adTemplate) {
                    return (
                      <AdCard
                        key={ad.adTemplate?.atID}
                        showTemplateButton
                        typeof=""
                        adData={{
                          Ad: { ...ad.adTemplate!, AdDbId: ad.id },
                          IsLocked: false,
                          IsPublic: false,
                          brandName: undefined,
                          shouldInvalidateCache: false,
                          DesiredWidth: desiredCardWidth,
                        }}
                      />
                    )
                  } else {
                    return (
                      <DiscoverAdCard
                        key={ad.id}
                        DesiredWidth={desiredCardWidth}
                        adData={{
                          ...ad,
                          id: ad.id ?? 0, // Ensure id is not null
                          reach: 0, // Provide a default or actual value
                          isSaved: true, // Provide a default or actual value
                        }}
                        IsPublic={false}
                        IsLocked={false}
                      />
                    )
                  }

                  return null
                })}

                {/** this is templates delete if all saved is removed */}
                {savedAdTemplates.map((ad) => (
                  <AdCard
                    key={ad?.atID}
                    showTemplateButton
                    typeof=""
                    adData={{
                      Ad: { ...ad, Liked: true },
                      IsLocked: false,
                      IsPublic: false,
                      brandName: undefined,
                      shouldInvalidateCache: false,
                      DesiredWidth: desiredCardWidth,
                    }}
                  />
                ))}
                {/** this is templates delete if all saved is removed */}
              </MasonryComponent>
            </TypedResponsiveMasonry>

            {/* Bottom scroll loader */}
            {hasNextAdsPage && (
              <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
                {isFetchingNextAdsPage && <Loader />}
              </div>
            )}
          </div>
        )}
      </div>
    </Stack>
  )
}
